import React, { useState } from 'react';
import { Layout, Typography, Card, Row, Col, Button, Space, Steps, Image, QRCode, Modal, Alert } from 'antd';
import {
  WechatOutlined,
  MobileOutlined,
  ScanOutlined,
  UserOutlined,
  SoundOutlined,
  TeamOutlined,
  MessageOutlined,
  PhoneOutlined,
  MailOutlined,
  QrcodeOutlined
} from '@ant-design/icons';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';

const { Content, Footer } = Layout;
const { Title, Paragraph, Text } = Typography;

const WechatPage = () => {
  const [qrModalVisible, setQrModalVisible] = useState(false);

  const features = [
    {
      icon: <SoundOutlined style={{ fontSize: '48px', color: '#07c160' }} />,
      title: '广播发送',
      description: '教师可通过小程序向班级发送音频、文字、图片等广播内容，实时推送到教室'
    },
    {
      icon: <TeamOutlined style={{ fontSize: '48px', color: '#07c160' }} />,
      title: '班级管理',
      description: '便捷管理多个班级，查看班级学生信息，统计班级各项数据'
    },
    {
      icon: <TeamOutlined style={{ fontSize: '48px', color: '#1890ff' }} />,
      title: '学生档案',
      description: '完整的学生档案管理，包含基本信息、家长联系方式等'
    },
    {
      icon: <MessageOutlined style={{ fontSize: '48px', color: '#52c41a' }} />,
      title: '积分管理',
      description: '学生积分奖励和扣分操作，实时统计排行榜，激励学生成长'
    },
    {
      icon: <UserOutlined style={{ fontSize: '48px', color: '#faad14' }} />,
      title: '考勤管理',
      description: '便捷的考勤打卡、请假审批，自动生成考勤统计报表'
    },
    {
      icon: <MessageOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />,
      title: '违纪管理',
      description: '记录学生违纪行为，跟踪处理进度，统计分析违纪数据'
    }
  ];

  const usageSteps = [
    {
      title: '扫码关注',
      description: '使用微信扫描小程序二维码'
    },
    {
      title: '授权登录',
      description: '授权微信信息并绑定教师账号'
    },
    {
      title: '选择班级',
      description: '选择要管理的班级或学校'
    },
    {
      title: '开始使用',
      description: '享受便捷的移动端广播服务'
    }
  ];

  const screenshots = [
    {
      title: '广播发送',
      description: '支持多种类型的广播内容发送',
      url: `${process.env.PUBLIC_URL}/broadcast.png`
    },
    {
      title: '班级管理',
      description: '便捷的班级和学生信息管理',
      url: `${process.env.PUBLIC_URL}/class-management.png`
    },
    {
      title: '学生管理',
      description: '便捷的学生档案和信息管理',
      url: `${process.env.PUBLIC_URL}/student-management.png`
    },
    {
      title: '个人中心',
      description: '个人信息和系统设置管理',
      url: `${process.env.PUBLIC_URL}/profile.png`
    },
    {
      title: '积分管理操作',
      description: '学生积分奖励和扣分操作',
      url: `${process.env.PUBLIC_URL}/points-operation.jpg`
    },
    {
      title: '积分管理统计',
      description: '积分数据统计和排行榜展示',
      url: `${process.env.PUBLIC_URL}/points-statistics.jpg`
    },
    {
      title: '考勤管理操作',
      description: '学生考勤打卡和请假管理',
      url: `${process.env.PUBLIC_URL}/attendance-operation.jpg`
    },
    {
      title: '考勤管理统计',
      description: '考勤数据统计和分析报表',
      url: `${process.env.PUBLIC_URL}/attendance-statistics.jpg`
    },
    {
      title: '违纪管理操作',
      description: '学生违纪记录和处理操作',
      url: `${process.env.PUBLIC_URL}/violation-operation.jpg`
    },
    {
      title: '违纪管理统计',
      description: '违纪数据统计和趋势分析',
      url: `${process.env.PUBLIC_URL}/violation-statistics.jpg`
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <PublicHeader />

      <Content style={{ padding: '24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* 页面标题 */}
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <Title level={1}>
              <WechatOutlined style={{ marginRight: '16px', color: '#07c160' }} />
              微信小程序
            </Title>
            <Paragraph style={{ fontSize: '18px', color: '#666' }}>
              教师的贴心好帮手，随时随地管理您的班级，让广播教学更加便捷高效
            </Paragraph>
          </div>

          {/* 小程序二维码和介绍 */}
          <Row gutter={[48, 48]} align="middle" style={{ marginBottom: '64px' }}>
            <Col xs={24} md={12}>
              <div style={{ textAlign: 'center' }}>
                <Card style={{ display: 'inline-block', padding: '24px' }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/wechatlogo.jpg`}
                    alt="微信小程序码"
                    style={{ width: '200px', height: '200px', objectFit: 'contain' }}
                    onError={(e) => {
                      console.error('二维码图片加载失败');
                    }}
                  />
                  <div style={{ marginTop: '16px' }}>
                    <Text strong>扫码体验小程序</Text>
                  </div>
                </Card>
                <div style={{ marginTop: '24px' }}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<QrcodeOutlined />}
                    onClick={() => setQrModalVisible(true)}
                  >
                    查看大图二维码
                  </Button>
                </div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <Title level={2}>随身携带的广播助手</Title>
              <Paragraph style={{ fontSize: '16px', lineHeight: '1.8' }}>
                教室小喇叭微信小程序为教师提供了便捷的移动端管理体验。
                作为教师的贴心好帮手，无需安装APP，通过微信即可随时随地管理班级广播，查看学生信息，
                发送通知消息，让教学管理更加高效便捷。
              </Paragraph>
              <Space direction="vertical" size="middle">
                <div>
                  <Text strong>✨ 即扫即用</Text> - 无需下载安装，微信扫码即可使用
                </div>
                <div>
                  <Text strong>📱 移动优化</Text> - 专为手机端优化的界面设计
                </div>
                <div>
                  <Text strong>🔒 安全可靠</Text> - 基于微信生态，数据传输安全加密
                </div>
                <div>
                  <Text strong>⚡ 实时同步</Text> - 与桌面端数据实时同步更新
                </div>
              </Space>
            </Col>
          </Row>

          {/* 功能特性 */}
          <div style={{ marginBottom: '64px' }}>
            <Title level={2} style={{ textAlign: 'center', marginBottom: '48px' }}>
              核心功能
            </Title>
            <Row gutter={[24, 24]}>
              {features.map((feature, index) => (
                <Col xs={24} sm={12} md={8} lg={8} key={index}>
                  <Card
                    hoverable
                    style={{
                      height: '100%',
                      textAlign: 'center',
                      border: '1px solid #e8f5e8'
                    }}
                    bodyStyle={{ padding: '32px 24px' }}
                  >
                    <div style={{ marginBottom: '16px' }}>
                      {feature.icon}
                    </div>
                    <Title level={4} style={{ marginBottom: '12px' }}>
                      {feature.title}
                    </Title>
                    <Paragraph style={{ color: '#666', margin: 0 }}>
                      {feature.description}
                    </Paragraph>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>

          {/* 使用步骤 */}
          <Card title="使用步骤" style={{ marginBottom: '64px' }}>
            <Steps
              direction="horizontal"
              current={-1}
              items={usageSteps}
              responsive={false}
            />
          </Card>


          {/* 界面截图部分已隐藏 */}

          {/* 常见问题 */}
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Card title="常见问题" style={{ height: '100%' }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Q: 如何绑定教师账号？</Text>
                    <br />
                    <Text type="secondary">A: 首次使用时，输入教师用户名和密码即可绑定</Text>
                  </div>
                  <div>
                    <Text strong>Q: 是否支持多个班级管理？</Text>
                    <br />
                    <Text type="secondary">A: 支持，可以管理您有权限的所有班级</Text>
                  </div>
                  <div>
                    <Text strong>Q: 小程序是否收费？</Text>
                    <br />
                    <Text type="secondary">A: 小程序免费使用，与您的教师账号权限一致</Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="联系我们" style={{ height: '100%' }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <PhoneOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    <Text>技术支持：17820345350</Text>
                  </div>
                  <div>
                    <MailOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    <Text>邮箱：284516383@qq.com</Text>
                  </div>
                  <div>
                    <WechatOutlined style={{ marginRight: '8px', color: '#07c160' }} />
                    <Text>微信客服：扫码添加客服微信</Text>
                  </div>
                  <Alert
                    message="使用提示"
                    description="如果遇到使用问题，可以联系您学校的系统管理员或技术支持团队"
                    type="info"
                    showIcon
                  />
                </Space>
              </Card>
            </Col>
          </Row>
        </div>
      </Content>

      <PublicFooter />

      {/* 二维码大图模态框 */}
      <Modal
        title="微信小程序二维码"
        open={qrModalVisible}
        onCancel={() => setQrModalVisible(false)}
        footer={null}
        width={400}
        centered
      >
        <div style={{ textAlign: 'center', padding: '24px' }}>
          <img
            src={`${process.env.PUBLIC_URL}/wechatlogo.jpg`}
            alt="微信小程序码"
            style={{ width: '300px', height: '300px', objectFit: 'contain' }}
          />
          <div style={{ marginTop: '24px' }}>
            <Text>请使用微信扫描二维码</Text>
            <br />
            <Text type="secondary">体验教室小喇叭小程序</Text>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default WechatPage;
